import React, { useState, useEffect } from "react";
import { entities, uploadFile, getDisplayMessage, openUploadInNewTab } from "@/api/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichTextEditor } from "@/components/rich-text/RichTextEditor.jsx";
import { Slider } from "@/components/ui/slider";
import { ArrowLeft, Save, Upload, X, Building2, User, CreditCard, Image as ImageIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { prepareAssinaturaFileForUploadWithMeta } from "@/utils/assinaturaBackground";
import { AssinaturaUploadProgressBar } from "@/components/empresa/AssinaturaUploadProgressBar";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";

export default function ConfiguracaoEmpresaPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { empresa } = useAuth();
  const empresaId = empresa?.id;
  const [uploadingField, setUploadingField] = useState(null);
  const [assinaturaProgress, setAssinaturaProgress] = useState(null);
  const [assinaturaElapsedSec, setAssinaturaElapsedSec] = useState(0);
  const [assinaturaJaSemFundo, setAssinaturaJaSemFundo] = useState(false);

  useEffect(() => {
    if (uploadingField !== "assinatura") {
      setAssinaturaElapsedSec(0);
      return undefined;
    }
    const t0 = Date.now();
    const id = window.setInterval(() => {
      setAssinaturaElapsedSec((Date.now() - t0) / 1000);
    }, 100);
    return () => clearInterval(id);
  }, [uploadingField]);

  const [config, setConfig] = useState({
    razao_social: "",
    cnpj: "",
    endereco_completo: "",
    municipio: "",
    uf: "",
    cep: "",
    telefone_1: "",
    telefone_2: "",
    email: "",
    inscricao_municipal: "",
    inscricao_estadual: "",
    banco_nome: "",
    banco_codigo: "",
    banco_agencia: "",
    banco_conta: "",
    socio_nome: "",
    socio_cargo: "",
    socio_nacionalidade: "",
    socio_profissao: "",
    socio_email: "",
    cabecalho_texto: "",
    rodape_texto: "",
    marca_dagua_transparencia: 10
  });

  const { data: configs = [] } = useQuery({
    queryKey: ["configuracao-empresa", empresaId],
    queryFn: () => entities.ConfiguracaoEmpresa.list(),
    enabled: !!empresaId,
  });

  useEffect(() => {
    if (configs.length > 0) {
      setConfig(configs[0]);
    }
  }, [configs]);

  const saveMutation = useMutation({
    mutationFn: async (data) => {
      if (configs.length > 0) {
        return entities.ConfiguracaoEmpresa.update(configs[0].id, data);
      } else {
        return entities.ConfiguracaoEmpresa.create(data);
      }
    },
    onSuccess: (savedConfig) => {
      // Garante que o formulário reflete imediatamente o que foi salvo
      if (savedConfig && empresaId) {
        setConfig(savedConfig);
        queryClient.setQueryData(["configuracao-empresa", empresaId], [savedConfig]);
      }
      if (empresaId) {
        queryClient.invalidateQueries({ queryKey: ["configuracao-empresa", empresaId] });
      }
      toast.success("Configurações salvas com sucesso!", {
        description: "Os dados da empresa foram atualizados e serão utilizados em todas as propostas geradas.",
        duration: 5000,
      });
    },
    onError: (error) => {
      console.error("Erro ao salvar configurações da empresa:", error);
      toast.error(getDisplayMessage(error, "Erro ao salvar configurações. Tente novamente."), { duration: 6000 });
    }
  });

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (field, file) => {
    setUploadingField(field);
    if (field === "assinatura" && !assinaturaJaSemFundo) {
      setAssinaturaProgress({ percent: 0, label: "Iniciando…" });
    }
    try {
      let fileToSend = file;
      if (field === "assinatura") {
        if (assinaturaJaSemFundo) {
          const t = (file.type || "").toLowerCase();
          if (t === "image/jpeg" || t === "image/jpg") {
            toast.warning("JPEG não tem transparência", {
              description: "Para fundo transparente use PNG ou WebP. O arquivo será enviado como está.",
              duration: 6000,
            });
          }
          setAssinaturaProgress({ percent: 25, label: "Enviando sem processar…" });
        } else {
          const { file: prepared, transparencyApplied } = await prepareAssinaturaFileForUploadWithMeta(file, {
            onProcessProgress: setAssinaturaProgress,
          });
          fileToSend = prepared;
          if (!transparencyApplied) {
            toast.info("Assinatura enviada sem remoção automática de fundo", {
              description:
                "O recorte automático não era seguro para esta imagem (fundo ou traço). Use PNG com fundo transparente ou foto com margem branca ao redor da assinatura.",
              duration: 7000,
            });
          }
        }
      }
      if (field === "assinatura") {
        setAssinaturaProgress({ percent: 88, label: "Enviando para o servidor…" });
      }
      const { file_url } = await uploadFile(fileToSend);
      if (field === "assinatura") {
        setAssinaturaProgress({ percent: 100, label: "Concluído" });
      }
      handleChange(`${field}_url`, file_url);
      handleChange(`${field}_nome`, fileToSend.name);
    } catch (error) {
      console.error("Erro ao fazer upload:", error);
      toast.error(getDisplayMessage(error, "Erro ao fazer upload do arquivo. Tente novamente."), { duration: 4000 });
    } finally {
      setUploadingField(null);
      setAssinaturaProgress(null);
    }
  };

  const handleRemoveFile = (field) => {
    handleChange(`${field}_url`, null);
    handleChange(`${field}_nome`, null);
  };

  const handleSave = () => {
    if (!config.razao_social || !config.cnpj) {
      toast.error("Campos obrigatórios não preenchidos", {
        description: "Por favor, preencha pelo menos a Razão Social e CNPJ antes de salvar.",
        duration: 4000,
      });
      return;
    }
    saveMutation.mutate(config);
  };

  const FileUploadButton = ({
    field,
    label,
    accept = "image/*",
    uploadingLabel = "Enviando...",
    processingSlot = null,
  }) => {
    const fileInputRef = React.useRef(null);
    const hasFile = config[`${field}_url`];
    const isUploading = uploadingField === field;

    return (
      <div className="space-y-2">
        <Label>{label}</Label>
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={(e) => {
            const file = e.target.files[0];
            if (file) handleFileUpload(field, file);
            e.target.value = "";
          }}
          className="hidden"
        />
        
        {hasFile ? (
          <div className="flex items-center gap-2">
            <div className="flex-1 p-3 border border-slate-200 rounded-lg bg-slate-50">
              <div className="flex items-center gap-2">
                <ImageIcon className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-slate-900 truncate">
                  {config[`${field}_nome`] || 'Arquivo enviado'}
                </span>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => openUploadInNewTab(config[`${field}_url`])}
            >
              Ver
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleRemoveFile(field)}
              className="text-red-600 hover:text-red-700"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        ) : isUploading && processingSlot ? (
          processingSlot
        ) : (
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="w-full"
          >
            <Upload className="w-4 h-4 mr-2" />
            {isUploading ? uploadingLabel : "Enviar Arquivo"}
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4 md:p-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(createPageUrl("Propostas"))}
            className="rounded-xl"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Configuração da Empresa</h1>
            <p className="text-slate-600 mt-1">Configure os dados que aparecerão nas propostas</p>
          </div>
        </div>

        <Alert className="mb-6 border-blue-200 bg-blue-50">
          <AlertDescription className="text-blue-900 text-sm">
            Estes dados serão utilizados automaticamente em todas as propostas geradas pelo sistema.
          </AlertDescription>
        </Alert>

        <div className="grid gap-6">
          <Card className="border-none shadow-xl bg-white/90 backdrop-blur-sm">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                Dados da Empresa
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label>Razão Social *</Label>
                  <Input
                    value={config.razao_social || ""}
                    onChange={(e) => handleChange('razao_social', e.target.value)}
                    placeholder="Digite aqui o nome da sua empresa"
                  />
                </div>

                <div className="space-y-2">
                  <Label>CNPJ *</Label>
                  <Input
                    value={config.cnpj || ""}
                    onChange={(e) => handleChange('cnpj', e.target.value)}
                    placeholder="00.000.000/0000-00"
                  />
                </div>

                <div className="space-y-2">
                  <Label>E-mail</Label>
                  <Input
                    type="email"
                    value={config.email || ""}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="email@email.com.br"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>Endereço Completo</Label>
                  <Input
                    value={config.endereco_completo || ""}
                    onChange={(e) => handleChange('endereco_completo', e.target.value)}
                    placeholder="Digite aqui seu endereço"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Município</Label>
                  <Input
                    value={config.municipio || ""}
                    onChange={(e) => handleChange('municipio', e.target.value)}
                    placeholder="Digite aqui seu Município"
                  />
                </div>

                <div className="space-y-2">
                  <Label>UF</Label>
                  <Input
                    value={config.uf || ""}
                    onChange={(e) => handleChange('uf', e.target.value)}
                    placeholder="  "
                    maxLength={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label>CEP</Label>
                  <Input
                    value={config.cep || ""}
                    onChange={(e) => handleChange('cep', e.target.value)}
                    placeholder="00000-000"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Telefone 1</Label>
                  <Input
                    value={config.telefone_1 || ""}
                    onChange={(e) => handleChange('telefone_1', e.target.value)}
                    placeholder="(XX) XXXXX-XXX"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Telefone 2</Label>
                  <Input
                    value={config.telefone_2 || ""}
                    onChange={(e) => handleChange('telefone_2', e.target.value)}
                    placeholder="(XX) XXXXX-XXXX"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Inscrição Municipal</Label>
                  <Input
                    value={config.inscricao_municipal || ""}
                    onChange={(e) => handleChange('inscricao_municipal', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Inscrição Estadual</Label>
                  <Input
                    value={config.inscricao_estadual || ""}
                    onChange={(e) => handleChange('inscricao_estadual', e.target.value)}
                    placeholder="Isento"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-xl bg-white/90 backdrop-blur-sm">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-purple-600" />
                Dados do Sócio/Representante Legal
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label>Nome Completo</Label>
                  <Input
                    value={config.socio_nome || ""}
                    onChange={(e) => handleChange('socio_nome', e.target.value)}
                    placeholder="Digite aqui o nome do sócio"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Cargo</Label>
                  <Input
                    value={config.socio_cargo || ""}
                    onChange={(e) => handleChange('socio_cargo', e.target.value)}
                    placeholder="Sócio Administrador"
                  />
                </div>

                <div className="space-y-2">
                  <Label>E-mail</Label>
                  <Input
                    type="email"
                    value={config.socio_email || ""}
                    onChange={(e) => handleChange('socio_email', e.target.value)}
                    placeholder="email@email.com.br"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Nacionalidade</Label>
                  <Input
                    value={config.socio_nacionalidade || ""}
                    onChange={(e) => handleChange('socio_nacionalidade', e.target.value)}
                    placeholder="Digite aqui sua nacionalidade, se Brasileiro"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Profissão</Label>
                  <Input
                    value={config.socio_profissao || ""}
                    onChange={(e) => handleChange('socio_profissao', e.target.value)}
                    placeholder="Digite aqui sua profissão, se Empresário"
                  />
                </div>

                <div className="space-y-3 md:col-span-2">
                  <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50/80 p-3">
                    <Checkbox
                      id="config-assinatura-sem-fundo"
                      checked={assinaturaJaSemFundo}
                      onCheckedChange={(v) => setAssinaturaJaSemFundo(v === true)}
                      className="mt-0.5"
                    />
                    <label
                      htmlFor="config-assinatura-sem-fundo"
                      className="text-sm text-slate-700 leading-snug cursor-pointer"
                    >
                      <span className="font-medium">Imagem já sem fundo</span>
                      <span className="block text-xs text-slate-500 mt-1">
                        Envia PNG ou WebP com transparência sem alterar o arquivo. Desmarque para remover
                        fundo claro automaticamente (foto em papel branco ou JPG).
                      </span>
                    </label>
                  </div>
                  <FileUploadButton
                    field="assinatura"
                    label="Assinatura Digital"
                    accept={
                      assinaturaJaSemFundo
                        ? "image/png,image/webp"
                        : "image/png,image/jpeg"
                    }
                    uploadingLabel={
                      assinaturaJaSemFundo ? "Enviando…" : "Removendo fundo e enviando..."
                    }
                    processingSlot={
                      assinaturaProgress ? (
                        <AssinaturaUploadProgressBar
                          label={assinaturaProgress.label}
                          percent={assinaturaProgress.percent}
                          elapsedSeconds={assinaturaElapsedSec}
                        />
                      ) : null
                    }
                  />
                  <p className="text-xs text-slate-500">
                    Com a opção desmarcada, remove o fundo claro pelas bordas (e por luminância se preciso).
                    Se não for seguro, a imagem original é enviada. A assinatura aparece acima do nome do
                    sócio na proposta.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-xl bg-white/90 backdrop-blur-sm">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-green-600" />
                Dados Bancários
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome do Banco</Label>
                  <Input
                    value={config.banco_nome || ""}
                    onChange={(e) => handleChange('banco_nome', e.target.value)}
                    placeholder="Digite aqui o Nome do Banco"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Código do Banco</Label>
                  <Input
                    value={config.banco_codigo || ""}
                    onChange={(e) => handleChange('banco_codigo', e.target.value)}
                    placeholder="  "
                  />
                </div>

                <div className="space-y-2">
                  <Label>Agência</Label>
                  <Input
                    value={config.banco_agencia || ""}
                    onChange={(e) => handleChange('banco_agencia', e.target.value)}
                    placeholder="  "
                  />
                </div>

                <div className="space-y-2">
                  <Label>Conta Corrente</Label>
                  <Input
                    value={config.banco_conta || ""}
                    onChange={(e) => handleChange('banco_conta', e.target.value)}
                    placeholder="  "
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-xl bg-white/90 backdrop-blur-sm">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-amber-600" />
                Personalização da Proposta
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <FileUploadButton 
                field="cabecalho" 
                label="Imagem de Cabeçalho" 
                accept="image/*"
              />

              <RichTextEditor
                id="cabecalho-texto-config"
                label="Texto de Cabeçalho (alternativa)"
                value={config.cabecalho_texto || ""}
                onChange={(html) => handleChange("cabecalho_texto", html)}
                placeholder="Texto que aparecerá no cabeçalho caso não envie imagem"
              />

              <FileUploadButton 
                field="rodape" 
                label="Imagem de Rodapé" 
                accept="image/*"
              />

              <RichTextEditor
                id="rodape-texto-config"
                label="Texto de Rodapé (alternativa)"
                value={config.rodape_texto || ""}
                onChange={(html) => handleChange("rodape_texto", html)}
                placeholder="Texto que aparecerá no rodapé caso não envie imagem"
              />

              <FileUploadButton 
                field="marca_dagua" 
                label="Marca D'água" 
                accept="image/*"
              />

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Label>Transparência da Marca D'água</Label>
                  <span className="text-sm font-semibold text-blue-600">
                    {config.marca_dagua_transparencia}%
                  </span>
                </div>
                <Slider
                  value={[config.marca_dagua_transparencia || 10]}
                  onValueChange={(value) => handleChange('marca_dagua_transparencia', value[0])}
                  min={0}
                  max={100}
                  step={5}
                  className="w-full"
                />
                <p className="text-xs text-slate-500">
                  Ajuste a transparência da marca d'água (0 = invisível, 100 = totalmente visível)
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-end gap-3 mt-6 mb-8">
          <Button
            variant="outline"
            onClick={() => navigate(createPageUrl("Propostas"))}
            disabled={saveMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={saveMutation.isPending}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Save className="w-4 h-4 mr-2" />
            {saveMutation.isPending ? 'Salvando...' : 'Salvar Configurações'}
          </Button>
        </div>
      </div>
    </div>
  );
}